import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { RotateCcw, Save } from 'lucide-react';
import { apiFetch } from '../../../../../shared/apiClient';
import { CustomSelect } from '../../../../../shared/components/CustomSelect';
import { EstimationApi } from '../../../../../features/analytics/estimation/estimationApi';
import type {
  EstimationOverride,
  EstimationSettings,
} from '../../../../../features/analytics/estimation/estimationApi';

interface ProductInfo {
  id: string;
  name: string;
}

export const EstimationTargetPanel: React.FC = () => {
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const [products, setProducts] = useState<ProductInfo[]>([]);
  const [orgDefaults, setOrgDefaults] = useState<EstimationSettings>({
    targetDeviationPct: 10,
    toleranceMultiplier: 4,
  });
  const [overrides, setOverrides] = useState<EstimationOverride[]>([]);
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null);

  const [localTarget, setLocalTarget] = useState(10);
  const [localMultiplier, setLocalMultiplier] = useState(4);

  const currentSaved = useMemo(() => {
    if (!selectedProductId) return orgDefaults;
    const o = overrides.find((ov) => ov.productId === selectedProductId);
    return o?.settings ?? orgDefaults;
  }, [selectedProductId, orgDefaults, overrides]);

  const isOverride = useMemo(
    () => !!selectedProductId && overrides.some((o) => o.productId === selectedProductId),
    [selectedProductId, overrides]
  );

  const hasChanges = useMemo(
    () =>
      localTarget !== currentSaved.targetDeviationPct ||
      localMultiplier !== currentSaved.toleranceMultiplier,
    [localTarget, localMultiplier, currentSaved]
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [orgRes, overridesRes, productsRes] = await Promise.all([
          EstimationApi.getOrgEstimationSettings(),
          EstimationApi.getEstimationOverrides(),
          apiFetch('/api/products'),
        ]);
        if (cancelled) return;
        setOrgDefaults(orgRes.settings);
        setOverrides(overridesRes.overrides);
        if (productsRes.ok) {
          const payload = await productsRes.json();
          setProducts(
            (payload.products ?? []).map((p: ProductInfo) => ({ id: p.id, name: p.name }))
          );
        }
        setLocalTarget(orgRes.settings.targetDeviationPct);
        setLocalMultiplier(orgRes.settings.toleranceMultiplier);
      } catch (err) {
        if (!cancelled)
          setError(err instanceof Error ? err.message : 'Failed to load estimation settings');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const switchProduct = useCallback(
    (productId: string | null) => {
      setSelectedProductId(productId);
      setError(null);
      setSaved(false);
      if (!productId) {
        setLocalTarget(orgDefaults.targetDeviationPct);
        setLocalMultiplier(orgDefaults.toleranceMultiplier);
      } else {
        const o = overrides.find((ov) => ov.productId === productId);
        const s = o?.settings ?? orgDefaults;
        setLocalTarget(s.targetDeviationPct);
        setLocalMultiplier(s.toleranceMultiplier);
      }
    },
    [orgDefaults, overrides]
  );

  const handleSave = useCallback(async () => {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const settings: EstimationSettings = {
        targetDeviationPct: localTarget,
        toleranceMultiplier: localMultiplier,
      };
      if (!selectedProductId) {
        const res = await EstimationApi.saveOrgEstimationSettings(settings);
        setOrgDefaults(res.settings);
      } else {
        await EstimationApi.saveEstimationOverride(selectedProductId, settings);
        const updated = await EstimationApi.getEstimationOverrides();
        setOverrides(updated.overrides);
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      await queryClient.invalidateQueries({ queryKey: ['estimation-settings-effective'] });
    } catch {
      setError('Failed to save estimation settings');
    } finally {
      setSaving(false);
    }
  }, [selectedProductId, localTarget, localMultiplier, queryClient]);

  const handleReset = useCallback(async () => {
    if (!selectedProductId) return;
    setSaving(true);
    setError(null);
    try {
      await EstimationApi.deleteEstimationOverride(selectedProductId);
      const updated = await EstimationApi.getEstimationOverrides();
      setOverrides(updated.overrides);
      setLocalTarget(orgDefaults.targetDeviationPct);
      setLocalMultiplier(orgDefaults.toleranceMultiplier);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      await queryClient.invalidateQueries({ queryKey: ['estimation-settings-effective'] });
    } catch {
      setError('Failed to reset estimation override');
    } finally {
      setSaving(false);
    }
  }, [selectedProductId, orgDefaults, queryClient]);

  if (loading) {
    return (
      <div className="settings-panel">
        <div style={{ padding: '8px 0', color: 'var(--color-text-secondary)', fontSize: '13px' }}>
          Loading estimation settings...
        </div>
      </div>
    );
  }

  return (
    <div className="settings-panel">
      <div className="settings-panel__header">
        <h3 className="settings-panel__title">Target deviation percentage</h3>
        <p className="settings-panel__description">
          Deviations within this percentage are considered on-target. Used for Delivery Confidence
          scoring.
        </p>
      </div>

      {error && (
        <div className="settings-callout settings-callout--danger" style={{ marginBottom: '16px' }}>
          <div className="settings-callout__body">{error}</div>
        </div>
      )}

      {/* Product selector */}
      <div style={{ marginTop: '12px', marginBottom: '16px' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '14px' }}>
          <span style={{ fontWeight: 500 }}>Editing settings for</span>
          <CustomSelect
            value={selectedProductId ?? ''}
            options={[
              { value: '', label: 'Organization default' },
              ...products.map((p) => ({
                value: p.id,
                label: `${p.name}${overrides.some((o) => o.productId === p.id) ? ' *' : ''}`,
              })),
            ]}
            onChange={(val) => switchProduct(val || null)}
            style={{ minWidth: '180px', fontSize: '14px' }}
          />
        </label>
        {selectedProductId && !isOverride && (
          <div
            style={{
              marginTop: '6px',
              fontSize: '12px',
              color: 'var(--color-text-secondary)',
            }}
          >
            Using org defaults. Edit and save to create a product-specific override.
          </div>
        )}
        {isOverride && (
          <div
            style={{
              marginTop: '6px',
              fontSize: '12px',
              color: 'var(--color-text-secondary)',
            }}
          >
            Product-specific override active.{' '}
            <span style={{ color: 'var(--color-text-muted)' }}>
              Org defaults: target = {orgDefaults.targetDeviationPct}%, multiplier ={' '}
              {orgDefaults.toleranceMultiplier}
            </span>
          </div>
        )}
      </div>

      <div style={{ marginTop: '12px' }}>
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            fontSize: '14px',
          }}
        >
          <span style={{ fontWeight: 500 }}>Target deviation (%)</span>
          <input
            type="number"
            min={1}
            max={100}
            value={localTarget}
            onChange={(e) => {
              const v = Number(e.target.value);
              if (!Number.isNaN(v)) setLocalTarget(Math.max(1, Math.min(100, v)));
            }}
            style={{
              width: '72px',
              padding: '6px 10px',
              borderRadius: '8px',
              border: '1px solid var(--glass-border)',
              background: 'var(--glass-bg)',
              fontSize: '14px',
            }}
          />
        </label>
      </div>

      <div style={{ marginTop: '20px' }}>
        <div className="settings-panel__header" style={{ marginBottom: '8px' }}>
          <h3 className="settings-panel__title">Tolerance multiplier</h3>
          <p className="settings-panel__description">
            Score reaches zero at deviation = target × (multiplier + 1). Default: 4 (zero at 5×
            target).
          </p>
        </div>
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            fontSize: '14px',
          }}
        >
          <span style={{ fontWeight: 500 }}>Multiplier</span>
          <input
            type="number"
            min={1}
            max={20}
            value={localMultiplier}
            onChange={(e) => {
              const v = Number(e.target.value);
              if (!Number.isNaN(v)) setLocalMultiplier(Math.max(1, Math.min(20, v)));
            }}
            style={{
              width: '72px',
              padding: '6px 10px',
              borderRadius: '8px',
              border: '1px solid var(--glass-border)',
              background: 'var(--glass-bg)',
              fontSize: '14px',
            }}
          />
        </label>
      </div>

      <div
        className="settings-callout"
        style={{
          marginTop: '20px',
          background: 'var(--glass-bg)',
          border: '1px solid var(--glass-border)',
          borderRadius: '10px',
          padding: '14px 16px',
          fontSize: '13px',
          lineHeight: 1.6,
          color: 'var(--color-text-secondary)',
        }}
      >
        <div style={{ fontWeight: 600, color: 'var(--color-text-primary)', marginBottom: '6px' }}>
          How scoring works
        </div>
        <div>Score = 30 × (1 − excess / spread), where spread = target × multiplier.</div>
        <div style={{ marginTop: '6px' }}>
          With target = <strong>{localTarget}%</strong> and multiplier ={' '}
          <strong>{localMultiplier}</strong>:
        </div>
        <ul style={{ margin: '4px 0 0 20px', padding: 0 }}>
          <li>
            Spread = {localTarget}% × {localMultiplier} ={' '}
            <strong>{localTarget * localMultiplier}%</strong>
          </li>
          <li>
            Deviation ≤ {localTarget}% → excess = 0 → <strong>score = 30</strong> (full)
          </li>
          <li>
            Deviation = {localTarget + Math.round((localTarget * localMultiplier) / 2)}% → excess ={' '}
            {Math.round((localTarget * localMultiplier) / 2)}% → score ={' '}
            {Math.round(
              30 *
                (1 -
                  Math.round((localTarget * localMultiplier) / 2) / (localTarget * localMultiplier))
            )}
          </li>
          <li>
            Deviation ≥ {localTarget * (localMultiplier + 1)}% → excess ≥ spread →{' '}
            <strong>score = 0</strong>
          </li>
        </ul>
      </div>

      <div
        style={{
          marginTop: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: '8px',
        }}
      >
        {isOverride && (
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            disabled={saving}
            onClick={handleReset}
            title="Reset to org defaults"
          >
            <RotateCcw size={13} />
            {saving ? 'Resetting…' : 'Reset to org default'}
          </button>
        )}
        {saved && <span style={{ fontSize: '0.82rem', color: 'var(--color-success)' }}>Saved</span>}
        <button
          type="button"
          className="btn btn-primary btn-sm"
          disabled={!hasChanges || saving}
          onClick={handleSave}
        >
          <Save size={14} />
          {saving ? 'Saving…' : 'Save targets'}
        </button>
      </div>
    </div>
  );
};
